import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import {
    Sparkles, ChevronRight, Scale, Coins, ListChecks,
    CalendarRange, Medal, Bell,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Bump this whenever you ship a meaningful release — the sheet will then
// pop up once for every user who hasn't seen this version yet.
const CURRENT_RELEASE = '3.62';
const STORAGE_KEY = 'bb_seen_whats_new_v';

/**
 * "What's new in BESTBUDDY" release notes sheet. Appears at most once per
 * version-bump per device. Users can also re-open it manually from
 * Settings → „Was ist neu?".
 *
 * Props:
 *   - open / onOpenChange: optional controlled mode (used by Settings link)
 */
export function WhatsNewSheet({ open: controlledOpen, onOpenChange }) {
    const { language } = useLanguage();
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = isControlled ? onOpenChange : setInternalOpen;

    // Auto-open once per release. We skip on /auth so first-time users
    // see onboarding first. We also defer when the first-day-plan bottom-sheet
    // is pending so brand-new users don't see two overlays back-to-back.
    // Once the sheet renders we IMMEDIATELY mark this release as seen — so
    // dismissing via swipe / ESC / nav-away never re-pops on the next visit.
    useEffect(() => {
        if (isControlled) return;
        if (typeof window === 'undefined') return;
        if (window.location.pathname.startsWith('/auth')) return;
        if (localStorage.getItem('bestbuddy_needs_onboarding') === 'true') return; // Nanouk onboarding first
        const seen = localStorage.getItem(STORAGE_KEY);
        const firstDayPending = localStorage.getItem('bestbuddy_first_day_plan_pending') === '1';
        if (firstDayPending) return; // first-day-plan wins; show next session
        if (seen !== CURRENT_RELEASE) {
            // Slight delay so the dashboard renders before the sheet pops up
            const t = setTimeout(() => {
                setInternalOpen(true);
                try {
                    localStorage.setItem(STORAGE_KEY, CURRENT_RELEASE);
                } catch (_e) { /* private mode — ignore */ }
            }, 1200);
            return () => clearTimeout(t);
        }
    }, [isControlled]);

    const handleClose = () => {
        try {
            localStorage.setItem(STORAGE_KEY, CURRENT_RELEASE);
        } catch (_e) { /* private mode etc. — ignore */ }
        setOpen(false);
    };

    const features = language === 'de' ? [
        {
            icon: Scale,
            color: 'text-amber-600',
            bg: 'bg-amber-50 dark:bg-amber-950/40',
            title: 'Energie-Konto — dein Fettpolster wird sichtbar',
            body: 'Jeder Tag startet nicht mehr bei Null: Überschüsse und Defizite werden fortlaufend aufsummiert und in Körperfett übersetzt (7\'700 kcal ≈ 1 kg ≈ 4 Päckli Butter 🧈). Mit Konto-Kurve, Butter-Zähler und Reset-Knopf — auf Dashboard & Ernährungs-Seite.',
        },
        {
            icon: Sparkles,
            color: 'text-sky-600',
            bg: 'bg-sky-50 dark:bg-sky-950/40',
            title: 'Nanouk trägt deine Last — wortwörtlich',
            body: 'Je grösser dein angesammeltes Fettpolster, desto schwerer leidet Nanouk: vom sprintenden Husky (Defizit 🔥) über den Butter-Rucksack bis zum Begraben-unter-dem-Butterberg. Überschreitest du eine neue Kilo-Stufe, meldet er sich per Push.',
        },
        {
            icon: Coins,
            color: 'text-yellow-600',
            bg: 'bg-yellow-50 dark:bg-yellow-950/40',
            title: 'Taler-System — Punkte statt Kalorien',
            body: 'In den Einstellungen aktivierbar: 18 Taler Tagesbudget (anpassbar). Protein & Ballaststoffe verbilligen, Fett & Kohlenhydrate verteuern. Echte Trainingseinheiten bringen bis zu +4 Bonus-Taler. Wirds knapp, warnt die Münzreihe — überziehst du, gibts rote Schulden-Münzen.',
        },
        {
            icon: ListChecks,
            color: 'text-green-600',
            bg: 'bg-green-50 dark:bg-green-950/40',
            title: 'Gewohnheits-Checkliste — 1 Tap loggt',
            body: 'Du bist ein Gewohnheitstier? Nanouk auch. Deine häufigsten Mahlzeiten der letzten 90 Tage erscheinen als Abhak-Liste auf der Ernährungs-Seite — ein Tap loggt inkl. Taler-Kosten, erneuter Tap machts rückgängig.',
        },
        {
            icon: CalendarRange,
            color: 'text-indigo-600',
            bg: 'bg-indigo-50 dark:bg-indigo-950/40',
            title: 'Nanouks Wochenrückblick',
            body: 'Jeden Montag wertet Nanouk deine Woche aus: Taler-Schnitt, Energie-Konto-Delta, bester & Sünden-Tag, Sport, Schlaf — plus ein persönliches KI-Fazit im Best-Buddy-Ton. Push am Montagmorgen, ältere Wochen jederzeit abrufbar.',
        },
        {
            icon: Medal,
            color: 'text-orange-500',
            bg: 'bg-orange-50 dark:bg-orange-950/40',
            title: 'Wochen-Streak mit Medaillen',
            body: 'Bleib mehrere Wochen in Folge im Taler-Zielbereich und sammle Medaillen: 2 Wochen 🥉, 4 Wochen 🥈, 8 Wochen 🥇. Eine Woche ohne Tracking oder mit Ausrutscher bricht die Serie — Langfristigkeit zählt!',
        },
        {
            icon: Bell,
            color: 'text-rose-600',
            bg: 'bg-rose-50 dark:bg-rose-950/40',
            title: 'Optimaler Kalorienbedarf berechnet',
            body: 'In den Einstellungen zeigt dir BESTBUDDY jetzt deinen wissenschaftlich berechneten Tagesbedarf (Mifflin-St-Jeor-Formel aus Gewicht, Grösse, Alter, Geschlecht & Fitness-Level) — mit einem Tap als Ziel übernehmbar.',
        },
    ] : [
        {
            icon: Scale, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950/40',
            title: 'Energy account — your fat reserve made visible',
            body: 'Days no longer reset to zero: surpluses and deficits accumulate continuously and translate into body fat (7,700 kcal ≈ 1 kg ≈ 4 butter packs 🧈). With balance curve, butter counter and reset — on dashboard & nutrition page.',
        },
        {
            icon: Sparkles, color: 'text-sky-600', bg: 'bg-sky-50 dark:bg-sky-950/40',
            title: 'Nanouk carries your load — literally',
            body: 'The bigger your accumulated fat reserve, the more Nanouk suffers: from sprinting husky (deficit 🔥) via butter backpack to buried-under-the-butter-mountain. Cross a new kilo level and he pings you via push.',
        },
        {
            icon: Coins, color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-950/40',
            title: 'Taler system — points instead of calories',
            body: 'Enable it in Settings: 18 Taler daily budget (adjustable). Protein & fiber discount, fat & carbs increase the price. Real workouts earn up to +4 bonus Taler. Running low? The coin row warns you — overdraw and you get red debt coins.',
        },
        {
            icon: ListChecks, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-950/40',
            title: 'Habit checklist — 1 tap logs',
            body: 'Creature of habit? So is Nanouk. Your most frequent meals from the last 90 days appear as a tick-off list on the nutrition page — one tap logs incl. Taler cost, another tap undoes it.',
        },
        {
            icon: CalendarRange, color: 'text-indigo-600', bg: 'bg-indigo-50 dark:bg-indigo-950/40',
            title: "Nanouk's weekly review",
            body: 'Every Monday Nanouk reviews your week: Taler average, energy account delta, best & sin day, sport, sleep — plus a personal AI verdict in best-buddy tone. Monday morning push, older weeks always available.',
        },
        {
            icon: Medal, color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-950/40',
            title: 'Weekly streak with medals',
            body: 'Stay in your Taler target range several weeks in a row and collect medals: 2 weeks 🥉, 4 weeks 🥈, 8 weeks 🥇. One untracked or off week breaks the run — consistency wins!',
        },
        {
            icon: Bell, color: 'text-rose-600', bg: 'bg-rose-50 dark:bg-rose-950/40',
            title: 'Optimal calorie intake calculated',
            body: 'Settings now show your scientifically calculated daily requirement (Mifflin-St Jeor formula from weight, height, age, gender & fitness level) — apply it as your goal with one tap.',
        },
    ];

    return (
        <Sheet open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(true); }}>
            <SheetContent side="bottom" className="h-[88vh] p-0 flex flex-col rounded-t-2xl">
                <SheetHeader className="p-4 border-b sticky top-0 bg-background z-10">
                    <SheetTitle className="flex items-center gap-2 text-base">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        {language === 'de' ? `Was ist neu — v${CURRENT_RELEASE}` : `What's new — v${CURRENT_RELEASE}`}
                    </SheetTitle>
                    <p className="text-xs text-muted-foreground">
                        {language === 'de'
                            ? '7 neue Features rund um Energie-Konto & Taler 🐺🧈'
                            : '7 new features around energy account & Taler 🐺🧈'}
                    </p>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="whats-new-body">
                    {features.map((f, idx) => {
                        const Icon = f.icon;
                        return (
                            <div
                                key={idx}
                                data-testid={`whats-new-card-${idx}`}
                                className={`flex gap-3 p-3 rounded-2xl ${f.bg} border border-border/40`}
                            >
                                <div className={`shrink-0 w-9 h-9 rounded-xl bg-background/80 flex items-center justify-center ${f.color}`}>
                                    <Icon className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm">{f.title}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{f.body}</p>
                                </div>
                            </div>
                        );
                    })}

                    <div className="pt-2 px-1">
                        <p className="text-[11px] text-muted-foreground text-center">
                            {language === 'de'
                                ? 'Tipp: Du kannst dieses Fenster jederzeit über Einstellungen → „Was ist neu?" wieder öffnen.'
                                : 'Tip: You can re-open this anytime via Settings → "What\'s new?".'}
                        </p>
                    </div>
                </div>

                <div className="p-3 border-t flex items-center gap-2">
                    <Button onClick={handleClose} className="flex-1" data-testid="whats-new-dismiss">
                        {language === 'de' ? 'Verstanden' : 'Got it'}
                        <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}

// Helper used by Settings → "What's new" link to force-open the sheet
export function openWhatsNew() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (_e) { /* ignore */ }
    window.location.reload();
}
